import clsx from 'clsx';

import { Tendency } from '@cast/shared-types';

import { LabelChip, LabelChipColor, LabelChipSize } from '../../../chips';
import { SxProps } from '../../../../types';
import { TendencyIcon } from '../TendencyIcon';

import { tendencyLabelChipClassNames } from './classNames';

export type TendencyLabelChipProps = {
  /** Chip label text — typically a numeric delta, e.g. `+12%` */
  label: string;
  /** Direction of change */
  tendency: Tendency;
  /** Whether an increase is a good thing — flips up/down colors */
  upIsPositive?: boolean;
  /** Icon placement relative to the label */
  iconPlacement?: 'start' | 'end';
  /** Chip size */
  size?: LabelChipSize;
  /** Additional CSS class */
  className?: string;
  /** `data-testid` attribute value */
  testId?: string;
  /** `sx` prop */
  sx?: SxProps;
};

const tendencyColors: Record<
  'default' | 'upIsPositive',
  Record<Tendency, LabelChipColor>
> = {
  default: {
    up: 'red',
    down: 'green',
    stable: 'grey',
  },
  upIsPositive: {
    up: 'green',
    down: 'red',
    stable: 'grey',
  },
};

export const TendencyLabelChip = ({
  label,
  tendency,
  upIsPositive = false,
  iconPlacement = 'start',
  size,
  className,
  testId,
  sx,
}: TendencyLabelChipProps) => {
  const icon = (
    <TendencyIcon
      variant="arrow"
      tendency={tendency}
      upIsPositive={upIsPositive}
    />
  );

  return (
    <LabelChip
      label={label}
      color={
        upIsPositive
          ? tendencyColors.upIsPositive[tendency]
          : tendencyColors.default[tendency]
      }
      size={size}
      slots={
        iconPlacement === 'start' ? { startIcon: icon } : { endIcon: icon }
      }
      className={clsx(
        tendencyLabelChipClassNames.root,
        tendencyLabelChipClassNames[tendency],
        tendencyLabelChipClassNames[iconPlacement],
        size && tendencyLabelChipClassNames[size],
        upIsPositive && tendencyLabelChipClassNames.upIsPositive,
        className
      )}
      testId={testId}
      sx={sx}
    />
  );
};

TendencyLabelChip.displayName = 'TendencyLabelChip';
